import type {
  TAvailableLanguageCodes,
  TMultipleSelectOption,
  TRankingOption,
  TReusableOption,
  TSingleSelectOption,
} from '@hubertai/screening-question-types';
import {
  EOptionStaticIds,
  EQuestionType,
  hasKey,
  hasLanguageKeyAndStringArrayValue,
  EEvaluationType,
} from '@hubertai/screening-question-types';
import type { PayloadAction } from '@reduxjs/toolkit';
import { current, nanoid } from '@reduxjs/toolkit';
import type { QuestionEditorsState } from './Question.Editor.Slice';
import { CREATE_MODES, ECreateQuestionMode } from 'components/Questionsv2/builder/context/create-question.context';
import type { TOptionItem, TSortableInput, TSortableItemBase } from 'components/TextBubbleDragAndDrop';

const getEmptyOption = (language: TAvailableLanguageCodes, label?: string, reference?: string): TSingleSelectOption => {
  return {
    id: nanoid(),
    label: { [language]: label ?? '' },
    ...(reference ? { reference } : {}),
    replies: { [language]: ['utter_confirm'] },
    enabled: { [language]: true },
    actions: { [language]: [] },
    hubiverse_tags: [],
    evaluation: EEvaluationType.Info,
  };
};

type Option = TSingleSelectOption | TMultipleSelectOption | TReusableOption | TRankingOption;

function isOptionItem(item: TSortableItemBase): item is TOptionItem {
  return 'label' in item && typeof item.id === 'string';
}

function areOptionItems(items: TSortableItemBase[]): items is TOptionItem[] {
  return items.every(isOptionItem);
}

export const questionOptionsReducer = {
  resetOptions: (state: QuestionEditorsState, action: PayloadAction<{ questionId: string }>) => {
    const { questionId } = action.payload;
    if (state.byId[questionId]) {
      const currentQuestion = state.byId[questionId];
      const questionConfig = currentQuestion.questionConfig;
      const withNewIds = (items: any[]) =>
        [ECreateQuestionMode.Copy, ECreateQuestionMode.InTemplateCopy].includes(currentQuestion.mode)
          ? items.map((item) => ({ ...item, id: nanoid() }))
          : items;
      const originalOptions: Option[] = hasKey(questionConfig, 'options') ? withNewIds(questionConfig.options) : [];

      state.byId[questionId] = {
        ...currentQuestion,
        options: [...currentQuestion.options, ...originalOptions],
      };
    }
  },

  clearOptions: (state: QuestionEditorsState, action: PayloadAction<{ questionId: string }>) => {
    const { questionId } = action.payload;
    if (!state.byId[questionId]) return;
    state.byId[questionId].options = [];
    state.byId[questionId].stateModified = true;
  },

  addOption: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      language: TAvailableLanguageCodes;
      label?: string;
    }>,
  ) => {
    const { questionId, label, language } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const x = structuredClone(current(question.options));
    x.push(getEmptyOption(language, label));
    question.options = [...x];
    question.stateModified = true;
  },

  /** Bulk-append options parsed from a pasted list. */
  addOptions: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      language: TAvailableLanguageCodes;
      options: { label: string; reference?: string }[];
    }>,
  ) => {
    const { questionId, language, options } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const x = structuredClone(current(question.options));
    for (const { label, reference } of options) {
      x.push(getEmptyOption(language, label, reference));
    }
    question.options = [...x];
    question.stateModified = true;
  },

  updateOption: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      language: TAvailableLanguageCodes;
      index: number;
      field: keyof TSingleSelectOption;
      value: any;
      bonusPoints?: number;
    }>,
  ) => {
    const { questionId, index, field, value, language, bonusPoints } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const x = structuredClone(current(question.options));

    if (['evaluation', 'bonus_points', 'role', 'reference'].includes(field)) {
      // @ts-ignore
      x[index][field] = value;
      if (value === 'bonus') {
        (x[index] as { bonus_points?: number }).bonus_points = bonusPoints;
      }
    } else {
      // @ts-ignore
      x[index][field][language] = value;
    }
    question.options = [...x];
    question.stateModified = true;
  },

  moveOption: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      fromIndex: number;
      toIndex: number;
    }>,
  ) => {
    const { questionId, fromIndex, toIndex } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const options = [...question.options];
    const option = options.splice(fromIndex, 1)[0];
    options.splice(toIndex, 0, option);

    question.options = [...options];
    question.stateModified = true;
  },

  toggleOptionEnabled: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      language: TAvailableLanguageCodes;
      index: number;
      value: boolean;
    }>,
  ) => {
    const { questionId, index, value, language } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const x = structuredClone(current(question.options));
    x[index].enabled[language] = value;
    question.options = [...x];
    question.stateModified = true;
  },

  removeOptionById: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      id: string;
    }>,
  ) => {
    const { questionId, id } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const x = structuredClone(current(question.options));
    const index = x.findIndex((o) => o.id === id);
    x.splice(index, 1);
    question.options = [...x];
    question.stateModified = true;
  },

  addStaticOptionAction: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      id: EOptionStaticIds;
      language: TAvailableLanguageCodes;
    }>,
  ) => {
    const { questionId, id, language } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const x = structuredClone(current(question.options));
    const y = x.filter((o) => o.id === id);

    if (y.length) {
      if (!question.optionWarnings) question.optionWarnings = [];
      question.optionWarnings.push(`${id} already exists.`);
      return;
    }

    const label =
      id === EOptionStaticIds.AllAbove
        ? 'All of the above'
        : id === EOptionStaticIds.NoneAbove
          ? 'None of the above'
          : id === EOptionStaticIds.Other
            ? 'Other'
            : '';

    if (label.length) {
      const newOption = getEmptyOption(language, label);
      // @ts-ignore
      newOption.value = id;
      x.push(newOption);
      question.options = [...x];
      question.stateModified = true;
    }
  },

  addActionToOption: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      index: number;
      language: TAvailableLanguageCodes;
    }>,
  ) => {
    const { questionId, index, language } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const x = structuredClone(current(question.options));

    if (hasLanguageKeyAndStringArrayValue(x[index].actions, language)) {
      x[index].actions[language]?.push('');
    } else {
      x[index].actions[language] = [''];
    }
    question.options = [...x];
    question.stateModified = true;
  },

  removeActionFromOption: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      optionIndex: number;
      actionIndex: number;
      language: TAvailableLanguageCodes;
    }>,
  ) => {
    const { questionId, optionIndex, actionIndex, language } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const x = structuredClone(current(question.options));

    if (hasLanguageKeyAndStringArrayValue(x[optionIndex].actions, language)) {
      x[optionIndex].actions[language]?.splice(actionIndex, 1);
    } else {
      if (!question.optionsErrors) question.optionsErrors = [];
      question.optionsErrors.push('removeActionFromOption error - contact Jacob.');
      return;
    }
    question.options = [...x];
    question.stateModified = true;
  },

  updateActionInOption: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      optionIndex: number;
      actionIndex: number;
      language: TAvailableLanguageCodes;
      value: string;
    }>,
  ) => {
    const { questionId, optionIndex, actionIndex, language, value } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const x = structuredClone(current(question.options));

    if (hasLanguageKeyAndStringArrayValue(x[optionIndex].actions, language)) {
      x[optionIndex].actions[language]![actionIndex] = value;
    } else {
      if (!question.optionsErrors) question.optionsErrors = [];
      question.optionsErrors.push('updateActionInOption error - contact Jacob.');
      return;
    }
    question.options = [...x];
    question.stateModified = true;
  },

  dragAndDropOption: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      items: TSortableInput;
      containerIds: string[];
      mode: ECreateQuestionMode;
      type: EQuestionType;
    }>,
  ) => {
    const { questionId, containerIds, mode, type, items } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];

    if (![...CREATE_MODES, ECreateQuestionMode.Edit, ECreateQuestionMode.InTemplateEdit].includes(mode)) {
      if (!question.optionsErrors) question.optionsErrors = [];
      question.optionsErrors.push('Aborted - invalid mode for editing options.');
      return;
    }

    if (![EQuestionType.SingleSelect, EQuestionType.MultipleSelect, EQuestionType.Ranking].includes(type)) {
      if (!question.optionsErrors) question.optionsErrors = [];
      question.optionsErrors.push('Aborted - must be a single/multiple select or ranking type question.');
      return;
    }

    if (!containerIds || !containerIds.length || containerIds.length !== 1 || containerIds[0] !== 'options') {
      if (!question.optionsErrors) question.optionsErrors = [];
      question.optionsErrors.push('Aborted - erroneous container.');
      return;
    }

    const optionsContainer = items.options;
    if (!optionsContainer) {
      if (!question.optionsErrors) question.optionsErrors = [];
      question.optionsErrors.push('Aborted - options container is missing.');
      return;
    }

    if (!areOptionItems(optionsContainer.items)) {
      if (!question.optionsErrors) question.optionsErrors = [];
      question.optionsErrors.push('Aborted - invalid option items.');
      return;
    }

    question.options = [...optionsContainer.items];
    question.stateModified = true;
  },

  addReplyOptions: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      fieldIndex: number;
      language: TAvailableLanguageCodes;
    }>,
  ) => {
    const { questionId, fieldIndex, language } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const x = structuredClone(current(question.options));

    if (!x || !x.length || typeof x[fieldIndex] === 'undefined') {
      if (!question.optionsErrors) question.optionsErrors = [];
      question.optionsErrors.push('Something is wrong.');
      return;
    }

    // Ensure replies exist
    if (!hasKey(x[fieldIndex], 'replies') || !x[fieldIndex].replies) {
      if (!question.optionsErrors) question.optionsErrors = [];
      question.optionsErrors.push('Replies object is missing.');
      return;
    }

    const replies = x[fieldIndex].replies;

    if (hasLanguageKeyAndStringArrayValue(replies, language)) {
      replies[language].push('utter_confirm');
    } else {
      replies[language] = ['utter_confirm'];
    }
    question.options = [...x];
    question.stateModified = true;
  },

  removeReplyOptions: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      fieldIndex: number;
      itemIndex: number;
      language: TAvailableLanguageCodes;
    }>,
  ) => {
    const { questionId, fieldIndex, itemIndex, language } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const x = structuredClone(current(question.options));

    if (!x || !x.length || typeof x[fieldIndex] === 'undefined') {
      if (!question.optionsErrors) question.optionsErrors = [];
      question.optionsErrors.push('Something is wrong.');
      return;
    }

    // Ensure replies exist
    if (!hasKey(x[fieldIndex], 'replies') || !x[fieldIndex].replies) {
      if (!question.optionsErrors) question.optionsErrors = [];
      question.optionsErrors.push('Replies object is missing.');
      return;
    }

    const replies = x[fieldIndex].replies;

    if (hasLanguageKeyAndStringArrayValue(replies, language)) {
      replies[language].splice(itemIndex, 1);
      question.options = [...x];
      question.stateModified = true;
    }
  },

  editReplyOptions: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      fieldIndex: number;
      itemIndex: number;
      newReply: string;
      language: TAvailableLanguageCodes;
    }>,
  ) => {
    const { questionId, fieldIndex, itemIndex, newReply, language } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const x = structuredClone(current(question.options));

    if (!x || !x.length || typeof x[fieldIndex] === 'undefined') {
      if (!question.optionsErrors) question.optionsErrors = [];
      question.optionsErrors.push('Something is wrong.');
      return;
    }

    // Ensure replies exist
    if (!hasKey(x[fieldIndex], 'replies') || !x[fieldIndex].replies) {
      if (!question.optionsErrors) question.optionsErrors = [];
      question.optionsErrors.push('Replies object is missing.');
      return;
    }

    const replies = x[fieldIndex].replies;

    if (!hasLanguageKeyAndStringArrayValue(replies, language)) {
      replies[language] = [];
    }

    const repliesByLanguage = replies[language] ?? (replies[language] = []);
    repliesByLanguage[itemIndex] = newReply;
    question.options = [...x];
    question.stateModified = true;
  },

  updateMinSelection: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      value: number;
    }>,
  ) => {
    const { questionId, value } = action.payload;

    if (!state.byId[questionId]) return;

    state.byId[questionId].min_selections = value;
    state.byId[questionId].stateModified = true;
  },

  updateMaxSelection: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      value: number;
    }>,
  ) => {
    const { questionId, value } = action.payload;

    if (!state.byId[questionId]) return;

    state.byId[questionId].max_selections = value;
    state.byId[questionId].stateModified = true;
  },
};
